import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table, Tag, Divider } from 'antd';
import { QuestionCircleOutlined, InfoCircleOutlined, CloseOutlined } from '@ant-design/icons';
import './KeyboardShortcutsHelp.css';

const KeyboardShortcutsHelp = ({ isDarkMode, isVisible, onClose }) => {
  const [internalVisible, setInternalVisible] = useState(false);
  
  // Use external visibility control if provided, otherwise use internal state
  const modalVisible = isVisible !== undefined ? isVisible : internalVisible;
  
  const handleClose = useCallback((visible) => {
    if (onClose) {
      onClose(visible);
    } else {
      setInternalVisible(visible);
    }
  }, [onClose]);

  // Keyboard shortcut to open help: Cmd/Ctrl + ? or Cmd/Ctrl + /
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + ? or Cmd/Ctrl + /
      if ((e.metaKey || e.ctrlKey) && (e.key === '?' || e.key === '/')) {
        // Prevent browser search if Shift is not held
        if (!e.shiftKey) {
          e.preventDefault();
          handleClose(true);
        }
      }
      // Close with Esc
      if (e.key === 'Escape' && modalVisible) {
        handleClose(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalVisible, handleClose]);

  const shortcuts = [
    {
      key: '1',
      category: 'Layout & Navigation',
      shortcuts: [
        {
          key: 'layout-1',
          shortcut: isDarkMode ? '⌘ B' : 'Ctrl + B',
          description: 'Toggle left panel (cycles: Expanded → Collapsed → Hidden)',
          mac: '⌘ B',
          windows: 'Ctrl + B'
        },
        {
          key: 'layout-2',
          shortcut: isDarkMode ? '⌘ ⇧ D' : 'Ctrl + Shift + D',
          description: 'Detach or dock chat window',
          mac: '⌘ ⇧ D',
          windows: 'Ctrl + Shift + D'
        },
        {
          key: 'layout-3',
          shortcut: 'Esc',
          description: 'Dock floating chat (when detached)',
          mac: 'Esc',
          windows: 'Esc'
        }
      ]
    },
    {
      key: '2',
      category: 'Chat',
      shortcuts: [
        {
          key: 'chat-1',
          shortcut: 'Enter',
          description: 'Send message',
          mac: 'Enter',
          windows: 'Enter'
        },
        {
          key: 'chat-2',
          shortcut: '⇧ Enter',
          description: 'New line in message',
          mac: '⇧ Enter',
          windows: 'Shift + Enter'
        }
      ]
    },
    {
      key: '3',
      category: 'Help',
      shortcuts: [
        {
          key: 'help-1',
          shortcut: isDarkMode ? '⌘ ?' : 'Ctrl + ?',
          description: 'Show this keyboard shortcuts help',
          mac: '⌘ ?',
          windows: 'Ctrl + ?'
        },
        {
          key: 'help-2',
          shortcut: isDarkMode ? '⌘ /' : 'Ctrl + /',
          description: 'Show this keyboard shortcuts help',
          mac: '⌘ /',
          windows: 'Ctrl + /'
        }
      ]
    }
  ];

  const columns = [
    {
      title: 'Shortcut',
      dataIndex: 'shortcut',
      key: 'shortcut',
      width: 180,
      render: (text) => (
        <Tag className={`shortcut-tag ${isDarkMode ? 'dark' : 'light'}`}>
          <code>{text}</code>
        </Tag>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description'
    }
  ];

  return (
    <>
      {/* Help Modal */}
      <Modal
        open={modalVisible}
        onCancel={() => handleClose(false)}
        footer={null}
        closable={true}
        className={`keyboard-shortcuts-modal ${isDarkMode ? 'dark' : 'light'}`}
        width={700}
        closeIcon={<CloseOutlined />}
      >
        <div className={`keyboard-shortcuts-content ${isDarkMode ? 'dark' : 'light'}`}>
          <div className="keyboard-shortcuts-header">
            <InfoCircleOutlined className="header-icon" />
            <h2>Keyboard Shortcuts</h2>
            <p className="subtitle">Speed up your workflow with these shortcuts</p>
          </div>

          <Divider />

          {shortcuts.map((category) => (
            <div key={category.key} className="shortcut-category">
              <h3 className="category-title">{category.category}</h3>
              <Table
                dataSource={category.shortcuts}
                columns={columns}
                pagination={false}
                showHeader={false}
                rowKey="key"
                className="shortcuts-table"
                size="small"
              />
            </div>
          ))}

          <Divider />

          <div className="keyboard-shortcuts-footer">
            <div className="platform-note">
              <QuestionCircleOutlined />
              <span>
                <strong>Platform:</strong> {isDarkMode ? 'Mac' : 'Windows/Linux'} shortcuts shown. 
                {isDarkMode ? ' Windows/Linux uses Ctrl instead of ⌘.' : ' Mac uses ⌘ instead of Ctrl.'}
              </span>
            </div>
            <div className="close-hint">
              Press <kbd>Esc</kbd> or click outside to close
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default KeyboardShortcutsHelp;

